import { useState } from "react"

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"

import {
  type AdminPlugin,
  type PluginDistribution,
  pluginsApi,
  type PluginSurface,
  type PluginVersion,
} from "@/api/plugins-api"
import { Loader2 } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Textarea } from "@/components/ui/textarea"

import { PluginAudiencePicker } from "./audience-picker"
import { pluginErrorCode, pluginErrorMessage, startAuthorization } from "./plugin-utils"
import { usePluginQueryKey } from "./use-plugin-query-key"

export function PluginAccessEditor({
  plugin,
  onSaved,
}: {
  plugin: AdminPlugin
  onSaved: () => void
}) {
  const { t } = useTranslation()
  const pluginKey = usePluginQueryKey()
  const distribution = useQuery({
    queryKey: pluginKey("distribution", plugin.id),
    queryFn: () => pluginsApi.distribution(plugin.id),
    staleTime: 0,
  })
  const versions = useQuery({
    queryKey: pluginKey("versions", plugin.id),
    queryFn: () => pluginsApi.versions(plugin.id),
  })
  if (distribution.isPending || versions.isPending)
    return <p role="status">{t("plugins.loading")}</p>
  if (!distribution.data || !versions.data) return <p role="alert">{t("plugins.error")}</p>
  return (
    <PluginAccessForm
      key={`${plugin.revision}:${distribution.data.distribution.revision}`}
      distribution={distribution.data.distribution}
      plugin={plugin}
      versions={versions.data.versions}
      onSaved={onSaved}
    />
  )
}

function PluginAccessForm({
  plugin,
  distribution,
  versions,
  onSaved,
}: {
  plugin: AdminPlugin
  distribution: PluginDistribution
  versions: PluginVersion[]
  onSaved: () => void
}) {
  const { t } = useTranslation()
  const pluginKey = usePluginQueryKey()
  const client = useQueryClient()
  const [visible, setVisible] = useState(plugin.audience)
  const [name, setName] = useState(plugin.name)
  const [description, setDescription] = useState(plugin.description)
  const [audience, setAudience] = useState(distribution.audience)
  const [enabled, setEnabled] = useState(plugin.enabled)
  const [selfInstall, setSelfInstall] = useState(plugin.self_install)
  const [managed, setManaged] = useState(distribution.enabled)
  const [surfaces, setSurfaces] = useState(plugin.surfaces)
  const [mode, setMode] = useState(distribution.credential_mode)
  const [versionId, setVersionId] = useState(
    distribution.version_id === "unpublished"
      ? (plugin.published_version ?? "")
      : distribution.version_id
  )
  const [secret, setSecret] = useState("")
  const [operation, setOperation] = useState("")
  const [args, setArgs] = useState("{}")
  const selectedVersion = versions.find((version) => version.id === versionId)
  const surfaceLabels = {
    explorer: "Explorer",
    deep: "Deep",
    flows: "Flows",
    voice: t("plugins.voice"),
    dash: "Dash",
  }
  const company = useQuery({
    queryKey: pluginKey("company", plugin.id),
    queryFn: () => pluginsApi.companyConnection(plugin.id),
  })
  const save = useMutation({
    mutationFn: async () => {
      await pluginsApi.configureAccess(plugin, distribution, {
        catalog: {
          name,
          description,
          enabled,
          self_install: selfInstall,
          audience: visible,
          surfaces,
        },
        distribution: {
          enabled: managed,
          audience,
          credential_mode: mode,
          version_id: versionId || "unpublished",
          connection_id: mode === "company" ? (company.data?.connection?.id ?? null) : null,
        },
      })
    },
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: pluginKey() })
      onSaved()
    },
    onError: () => client.invalidateQueries({ queryKey: pluginKey("admin") }),
  })
  const connect = useMutation({
    mutationFn: async () => {
      if (selectedVersion?.auth.kind === "oauth" || selectedVersion?.auth.kind === "entra") {
        startAuthorization((await pluginsApi.companyOAuth(plugin.id)).authorization_url)
        return
      }
      const credential = secret
      setSecret("")
      return pluginsApi.connectCompany(
        plugin.id,
        selectedVersion?.auth.kind === "api_key" ? "api_key" : "bearer",
        credential
      )
    },
    onSuccess: () => client.invalidateQueries({ queryKey: pluginKey("company", plugin.id) }),
  })
  const disconnect = useMutation({
    mutationFn: () => pluginsApi.disconnectCompany(plugin.id),
    onSuccess: () => client.invalidateQueries({ queryKey: pluginKey() }),
  })
  const test = useMutation({
    mutationFn: () =>
      pluginsApi.test(plugin.id, operation, JSON.parse(args) as Record<string, unknown>),
  })
  const error = save.error ?? connect.error ?? disconnect.error ?? test.error
  return (
    <div className="space-y-5" data-testid="plugin-access-editor">
      <div className="space-y-2">
        <Label htmlFor="plugin-edit-name">{t("plugins.name")}</Label>
        <Input
          id="plugin-edit-name"
          value={name}
          onChange={(event) => setName(event.target.value)}
        />
        <Label htmlFor="plugin-edit-description">{t("plugins.description")}</Label>
        <Textarea
          id="plugin-edit-description"
          value={description}
          onChange={(event) => setDescription(event.target.value)}
        />
      </div>
      <label className="flex items-center gap-2 text-sm">
        <Checkbox checked={enabled} onCheckedChange={(value) => setEnabled(value === true)} />
        {t("plugins.enabled")}
      </label>
      <fieldset className="space-y-2">
        <legend className="mb-2 font-medium">{t("plugins.surfaces")}</legend>
        <div className="flex flex-wrap gap-4">
          {(["explorer", "deep", "voice", "flows", "dash"] as PluginSurface[]).map((surface) => (
            <label key={surface} className="flex items-center gap-2 text-sm">
              <Checkbox
                checked={surfaces.includes(surface)}
                onCheckedChange={(value) =>
                  setSurfaces((current) =>
                    value ? [...current, surface] : current.filter((item) => item !== surface)
                  )
                }
              />
              {surfaceLabels[surface]}
            </label>
          ))}
        </div>
        <p className="text-muted-foreground text-xs">{t("plugins.voiceDescription")}</p>
      </fieldset>
      <div className="space-y-2">
        <h3 className="font-medium">{t("plugins.catalogAudience")}</h3>
        <PluginAudiencePicker value={visible} onChange={setVisible} />
        <label className="flex items-center gap-2 text-sm">
          <Checkbox
            checked={selfInstall}
            onCheckedChange={(value) => setSelfInstall(value === true)}
          />
          {t("plugins.allowSelfInstall")}
        </label>
      </div>
      <div className="space-y-3">
        <label className="flex items-center gap-2 font-medium">
          <Checkbox checked={managed} onCheckedChange={(value) => setManaged(value === true)} />
          {t("plugins.distribute")}
        </label>
        {managed && (
          <>
            <PluginAudiencePicker value={audience} onChange={setAudience} />
            <p className="text-muted-foreground text-xs">{t("plugins.futureMembers")}</p>
          </>
        )}
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-2">
          <Label>{t("plugins.version")}</Label>
          <Select value={versionId} onValueChange={setVersionId}>
            <SelectTrigger aria-label={t("plugins.version")}>
              <SelectValue placeholder={t("plugins.version")} />
            </SelectTrigger>
            <SelectContent>
              {versions.map((version) => (
                <SelectItem key={version.id} value={version.id}>
                  {version.version}
                  {version.id === plugin.published_version ? ` · ${t("plugins.latest")}` : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>{t("plugins.accountMode")}</Label>
          {selectedVersion?.auth.kind === "none" ? (
            <p className="text-muted-foreground text-sm">{t("plugins.auth.none")}</p>
          ) : (
            <Select
              value={mode}
              onValueChange={(value) => setMode(value as "personal" | "company")}
            >
              <SelectTrigger aria-label={t("plugins.accountMode")}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="personal">{t("plugins.personalAccount")}</SelectItem>
                <SelectItem value="company">{t("plugins.companyAccount")}</SelectItem>
              </SelectContent>
            </Select>
          )}
        </div>
      </div>
      {mode === "personal" && selectedVersion?.auth.kind !== "none" && (
        <p className="text-muted-foreground text-sm">{t("plugins.personalDistribution")}</p>
      )}
      {mode === "company" && selectedVersion?.auth.kind !== "none" && (
        <div className="space-y-3 rounded-lg border p-3">
          <p className="text-sm">
            {t(`plugins.connection.${company.data?.connection?.status ?? "disconnected"}`)}
          </p>
          {selectedVersion?.auth.kind !== "oauth" && selectedVersion?.auth.kind !== "entra" && (
            <>
              <Label htmlFor="company-plugin-secret">{t("plugins.credential")}</Label>
              <Input
                autoComplete="off"
                id="company-plugin-secret"
                type="password"
                value={secret}
                onChange={(event) => setSecret(event.target.value)}
              />
            </>
          )}
          <Button
            variant="outline"
            disabled={
              connect.isPending ||
              (!secret &&
                selectedVersion?.auth.kind !== "oauth" &&
                selectedVersion?.auth.kind !== "entra")
            }
            onClick={() => connect.mutate()}
          >
            {t("plugins.connectCompany")}
          </Button>
          {company.data?.connection?.status === "connected" && (
            <Button
              disabled={disconnect.isPending}
              variant="ghost"
              onClick={() => disconnect.mutate()}
            >
              {t("plugins.disconnect")}
            </Button>
          )}
        </div>
      )}
      {error && (
        <p className="text-destructive text-sm" role="alert">
          {pluginErrorMessage(t, pluginErrorCode(error))}
        </p>
      )}
      <Button disabled={save.isPending || (managed && !versionId)} onClick={() => save.mutate()}>
        {save.isPending && <Loader2 className="mr-2 size-4 animate-spin" />}
        {t("plugins.save")}
      </Button>
      {selectedVersion && (
        <details className="rounded-lg border p-3">
          <summary className="cursor-pointer text-sm font-medium">{t("plugins.test")}</summary>
          <div className="mt-3 space-y-3">
            <p className="text-muted-foreground text-xs">{t("plugins.testDescription")}</p>
            <Select value={operation} onValueChange={setOperation}>
              <SelectTrigger aria-label={t("plugins.operation")}>
                <SelectValue placeholder={t("plugins.operation")} />
              </SelectTrigger>
              <SelectContent>
                {selectedVersion.operations.map((op) => (
                  <SelectItem key={op.id} value={op.id}>
                    {op.id}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Label htmlFor="plugin-test-args">{t("plugins.arguments")}</Label>
            <Textarea
              id="plugin-test-args"
              value={args}
              onChange={(event) => setArgs(event.target.value)}
            />
            <Button
              disabled={!operation || test.isPending}
              variant="outline"
              onClick={() => test.mutate()}
            >
              {t("plugins.test")}
            </Button>
            {test.data && (
              <p role="status">
                {test.data.success
                  ? t("plugins.testPassed")
                  : pluginErrorMessage(t, test.data.error)}
              </p>
            )}
          </div>
        </details>
      )}
    </div>
  )
}
